/**
 * Suggestion chips above the composer. Rule chips carry a severity dot, LLM
 * chips the assistant's face; hovering shows why the chip is there. A prompt chip
 * sends its prompt to the agent (new thread + workspace); a navigate chip just
 * goes. With `onDismiss`, an alert (rule) chip also gets a small "x" that
 * removes it without running its action; curated chips never do.
 */
import { RefreshCw, X } from "lucide-react";
import { Fragment, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { Suggestions } from "@/components/ai-elements/suggestion";
import { PromptChip } from "@/components/surface";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { cn } from "@/lib/utils";

import type { Recommendation, RecommendationSeverity } from "../types";

type Props = {
  items: Recommendation[];
  /** The suggesting assistant's avatar seed (its scope key). */
  seed?: string;
  isLoading?: boolean;
  onPrompt: (prompt: string) => void;
  onRegenerate?: () => void;
  /** Makes alert chips dismissable; omitted, no chip shows a dismiss button. */
  onDismiss?: (rec: Recommendation) => void;
  /** Where the chips sit: centred under a centred hero, or from the left. */
  align?: "center" | "start";
  className?: string;
};

const DOT: Record<RecommendationSeverity, string> = {
  urgent: "bg-destructive",
  warn: "bg-warn",
  info: "bg-info"
};

export const RecommendedActions: FC<Props> = ({
  items,
  seed = "assistant",
  isLoading = false,
  onPrompt,
  onRegenerate,
  onDismiss,
  align = "center",
  className
}) => {
  const navigate = useNavigate();

  if (isLoading && !items.length) {
    return (
      <div
        className={cn(
          "flex flex-wrap gap-2",
          align === "center" ? "justify-center" : "justify-start",
          className
        )}
        data-testid="recommended-actions-loading"
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-7 w-40 rounded-sm" />
        ))}
      </div>
    );
  }
  if (!items.length) return null;

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2",
        align === "center" ? "items-center" : "items-start",
        className
      )}
      data-testid="recommended-actions"
    >
      <Suggestions
        className={cn(
          "w-full flex-wrap",
          align === "center" ? "justify-center" : "justify-start"
        )}
      >
        {items.map((rec) => {
          const run = () => {
            if (rec.action.type === "prompt") onPrompt(rec.action.prompt);
            else navigate(rec.action.path);
          };
          const dismissable = Boolean(onDismiss) && rec.source === "rule";
          const chip = (
            <PromptChip
              suggestion={rec.title}
              onClick={run}
              className={cn(dismissable && "max-w-full pr-6")}
              data-testid="recommended-action"
              data-source={rec.source}
              data-severity={rec.severity}
            >
              {rec.source !== "rule" ? (
                <AgentMoodAvatar
                  animated
                  size="xs"
                  className="size-3.5 rounded-xs"
                  agent={{ key: seed }}
                />
              ) : (
                <span
                  className={cn(
                    "inline-block size-1.5 shrink-0 rounded-full",
                    DOT[rec.severity] ?? DOT.info
                  )}
                  aria-hidden
                />
              )}
              <span className="truncate">{rec.title}</span>
            </PromptChip>
          );
          const withTip = rec.reason ? (
            <Tooltip>
              <TooltipTrigger asChild>{chip}</TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                {rec.reason}
              </TooltipContent>
            </Tooltip>
          ) : (
            chip
          );
          if (!dismissable) return <Fragment key={rec.id}>{withTip}</Fragment>;
          // The "x" sits beside the chip, not inside it (a button can't hold a
          // button). It shows on hover or focus, and always on touch screens;
          // it stays in the tab order either way.
          return (
            <span
              key={rec.id}
              className="group/chip relative inline-flex max-w-72 shrink-0"
              data-testid="recommended-action-item"
            >
              {withTip}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-1/2 right-1 size-4 -translate-y-1/2 rounded-xs p-0 text-muted-foreground opacity-0 group-focus-within/chip:opacity-100 group-hover/chip:opacity-100 pointer-coarse:opacity-100 hover:text-foreground focus-visible:opacity-100"
                aria-label={`Dismiss Alert: ${rec.title}`}
                onClick={() => onDismiss?.(rec)}
                data-testid="recommended-action-dismiss"
              >
                <X className="size-3" aria-hidden />
              </Button>
            </span>
          );
        })}
      </Suggestions>
      {onRegenerate ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 rounded-sm px-2 text-xs text-muted-foreground"
          onClick={onRegenerate}
          disabled={isLoading}
          data-testid="recommended-actions-refresh"
        >
          <RefreshCw className={cn("size-3", isLoading && "animate-spin")} />
          New Suggestions
        </Button>
      ) : null}
    </div>
  );
};
